/**
 * PROBAR UNA REGLA SOBRE UN CLIENTE, desde la Documentación (10-10).
 *
 * El servidor hace la prueba y la cuenta (backend/core/probar_regla.py). Aquí solo se ordena
 * lo que manda para pintarlo: qué hizo la regla probada, qué otras saltaron a la vez y los
 * correos con su texto. Las frases de los cambios son las de siempre (lib/cambiosDelReloj.js).
 *
 * Lógica pura: sin React y sin red.
 */
import { agruparCambios } from './cambiosDelReloj';
import { reglasEnClaro } from './documentacion';

const lista = (x) => (Array.isArray(x) ? x : []);

/**
 * Los cambios del disparo, en líneas: `suya` es la de la regla probada (o null si no dejó
 * nada apuntado), `aLaVez` las de las otras reglas que saltaron en ese instante y `sueltos`
 * lo que no es de ninguna regla.
 */
export const lineasDelResultado = (resultado) => {
    const grupos = agruparCambios(lista(resultado?.cambios), { ocultar: ['hasta'] });
    return {
        suya: grupos.find((g) => g.regla === resultado?.regla) || null,
        aLaVez: grupos.filter((g) => g.regla && g.regla !== resultado?.regla),
        sueltos: grupos.filter((g) => !g.regla && g.titulo),
    };
};

/** Los correos del disparo que traen texto (o el porqué de no tenerlo), con su regla. */
export const correosDelResultado = (resultado) => lista(resultado?.cambios)
    .filter((c) => c && c.correo)
    .map((c) => ({ regla: c.regla, correo: c.correo }));

/**
 * «Para colocarlo saltó antes la regla 9...» / «Para colocarlo saltaron antes las reglas 9 y
 * 12...»: las que saltaron antes, para colocar al cliente. '' si no saltó ninguna.
 */
export const fraseDeColocacion = (resultado) => {
    const numeros = lista(resultado?.otras).filter((o) => o.cuando === 'para colocarlo').map((o) => o.numero);
    if (numeros.length === 0) return '';
    return `Para colocarlo ${numeros.length === 1 ? 'saltó' : 'saltaron'} antes ${reglasEnClaro(numeros)}, sin mandar `
        + `${numeros.length === 1 ? 'su correo ni crear su tarea' : 'sus correos ni crear sus tareas'}.`;
};

/**
 * La etiqueta del resultado: qué se hizo de verdad. Si se pidió ejecutar y la regla no iba a
 * saltar, no es un ensayo: no se ha ejecutado, y se dice.
 */
export const etiquetaDelResultado = (resultado) => {
    if (resultado?.escrito) return { texto: 'Ejecutada en su ficha', tono: 'hecha' };
    if (resultado?.no_ejecutada) return { texto: 'No se ha ejecutado: no iba a saltar', tono: 'no' };
    return { texto: 'Ensayo: no se ha escrito nada', tono: 'ensayo' };
};

/**
 * Lo que se dice al devolver. Si se forzó, o hubo campos que no se tocaron, el cliente NO ha
 * quedado como antes: se dice con la frase del servidor y como aviso, no como «hecho».
 */
export const mensajeDeDevolucion = (hecho) => {
    const h = hecho || {};
    if (!h.devuelto) return { texto: h.frase || 'No había nada que devolver.', aviso: false };
    const conPeros = !!h.forzado || lista(h.sin_tocar).length > 0;
    return { texto: h.frase || 'Devuelto a como estaba antes de la prueba.', aviso: conPeros };
};

/** Lo que se dice de un cliente que tiene una prueba sin devolver. '' si no tiene. */
export const pruebaSinDevolver = (enPrueba) => {
    if (!enPrueba) return '';
    const numeros = lista(enPrueba.reglas).map((id) => Number(String(id).replace(/^R/, ''))).filter((n) => !Number.isNaN(n));
    const cuales = reglasEnClaro(numeros);
    return cuales ? `Tiene una prueba sin devolver: ${cuales}` : 'Tiene una prueba sin devolver';
};

/**
 * Lo que la lista dice de un cliente. Lo decide el servidor con el mismo ensayo que hace al
 * ejecutar: `se_puede: false` con su `motivo` (no se le puede elegir) o un `aviso` (se le
 * puede elegir, pero la regla no saltaría). Aquí solo se ordena para pintarlo.
 */
export const estadoEnLaLista = (cliente) => {
    const c = cliente || {};
    // QUIEN TIENE UNA PRUEBA SIN DEVOLVER SE PUEDE ELEGIR SIEMPRE (`pulsable`), aunque ya no se
    // le pueda probar esta regla: si no, no habría cómo devolverlo desde aquí. Lo que queda
    // apagado para él es mirar y ejecutar (`elegible`).
    if (c.se_puede === false) {
        return { elegible: false, pulsable: !!c.en_prueba, texto: c.motivo || 'Con este cliente no se puede probar esta regla.' };
    }
    // Del que está en pausa y sí se puede elegir, se avisa: su semana no se mueve sola.
    const avisos = [c.en_pausa ? 'Está en pausa.' : '', c.aviso || ''].filter(Boolean);
    return { elegible: true, pulsable: true, texto: avisos.join(' ') };
};

/** «3 de estos clientes no se pueden elegir: debajo de cada uno dice por qué.» '' si son todos. */
export const cuantosNoSePueden = (n) => {
    if (!n) return '';
    return n === 1 ? 'Con 1 de estos clientes no se puede probar ahora: debajo dice por qué.'
        : `Con ${n} de estos clientes no se puede probar ahora: debajo de cada uno dice por qué.`;
};

/**
 * La frase que se enseña si la petición falla. Estas rutas contestan con una frase ya humana
 * cuando la prueba no se puede hacer (409) o falta un dato (422): esa se enseña. Cualquier
 * otra cosa va a la consola y en pantalla sale la frase fija.
 */
export const fraseDelFallo = (error, fija) => {
    const res = error && error.response;
    const detalle = res && res.data && res.data.detail;
    if (res && (res.status === 409 || res.status === 422) && typeof detalle === 'string' && detalle.trim()) {
        return detalle.trim();
    }
    return fija;
};
